import React, { useEffect, forwardRef } from 'react';
import type { Selection } from '@heroui/react';
import { Select, SelectItem, SelectProps } from '@heroui/react';
import cn from 'classnames';
import styles from './CustomSelect.module.scss';

interface CustomSelectProps extends Omit<SelectProps, 'style' | 'children'> {
  style: 'black' | 'color' | 'purple' | 'cream' | 'creamCompact';
  items: { key: string; label: string }[];
  defaultValue?: string[];
  selectedKeys?: Selection;
  errorMessage?: string;
  showError?: boolean;
  getSelectedMultiValues?: (value: Selection) => void;
  disableTextCurrent?: boolean;
  hideLabel?: boolean;
}

export const CustomSelect = forwardRef<HTMLSelectElement, CustomSelectProps>((props, ref) => {
  const {
    items,
    style,
    defaultValue = [],
    getSelectedMultiValues,
    errorMessage,
    showError = false,
    selectedKeys,
    disableTextCurrent = false,
    hideLabel = false,
    ...selectProps
  } = props;

  const [value, setValue] = React.useState<Selection>(selectedKeys || new Set(defaultValue));

  const classnamesBlackSelect = {
    base: styles.blackBase,
    innerWrapper: styles.blackInnerWrapper,
    popoverContent: styles.blackPopoverContent,
    listbox: styles.blackListbox,
  };

  const classnamesColorSelect = {
    base: 'max-w-[185px]',
    innerWrapper: styles.colorInnerWrapper,
    popoverContent: styles.colorPopoverContent,
    listbox: styles.colorListbox,
  };

  const classnamesPurpleSelect = {
    base: 'max-w-[185px]',
    innerWrapper: styles.purpleInnerWrapper,
    popoverContent: styles.purplePopoverContent,
    listbox: styles.purpleListbox,
  };

  const classnamesCreamSelect = {
    innerWrapper: styles.blackInnerWrapper,
    popoverContent: styles.blackPopoverContent,
    listbox: styles.blackListbox,
  };

  const getColorTriggerStyle = (
    selectedValue: Selection,
    items: { key: string; label: string }[],
  ) => {
    const selectedKey = Array.from(selectedValue)[0];
    if (selectedKey === undefined) {
      return '';
    }

    const selectedItem = items.find((item) => item.key === selectedKey.toString());
    const label = selectedItem?.label || '';

    if (['Approved', 'Sent', 'Paid', 'Completed', 'Active'].includes(label)) {
      return styles.colorTriggerGreen;
    }
    if (
      [
        'Pending moderation',
        'Scheduled',
        'Awaiting payment',
        'Awaiting review',
        'Hidden',
      ].includes(label)
    ) {
      return styles.colorTriggerYellow;
    }
    if (['Rejected', 'Cancelled', 'In progress', 'Closed'].includes(label)) {
      return styles.colorTriggerRed;
    } else {
      return styles.colorTrigger;
    }
  };

  const [isUserInteraction, setIsUserInteraction] = React.useState(false);
  const [previousValue, setPreviousValue] = React.useState<Selection>(new Set());

  useEffect(() => {
    if (getSelectedMultiValues && isUserInteraction) {
      const currentValueStr = Array.from(value).sort().join(',');
      const previousValueStr = Array.from(previousValue).sort().join(',');

      if (currentValueStr !== previousValueStr) {
        getSelectedMultiValues(value);
        setPreviousValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSelectedMultiValues, value, isUserInteraction, previousValue]);

  const handleSelectionChange = (newValue: Selection) => {
    setValue(newValue);
    setIsUserInteraction(true);
    if (selectProps.onSelectionChange) {
      selectProps.onSelectionChange(newValue);
    }
  };

  useEffect(() => {
    if (selectedKeys) {
      const currentKeys = Array.from(value instanceof Set ? value : []).sort().join(',');
      const newKeys = Array.from(selectedKeys instanceof Set ? selectedKeys : []).sort().join(',');
      if (currentKeys !== newKeys) {
        setValue(selectedKeys);
      }
    } else if (value instanceof Set && value.size > 0) {
      setValue(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys]);

  const isInvalid = !!errorMessage;
  // The error is rendered below the Select instead of HeroUI's helper: with a helper an
  // outside label loses its absolute position and the field shifts down by the label height.
  // `isInvalid` is still passed: a controlled value keeps HeroUI from showing the browser's
  // own messages of the hidden native <select required>
  // A pressed trigger doesn't take focus (React Aria prevents it), so on close the list gave focus
  // back to the field focused before it — in a scrolled modal a date or time field scrolled itself
  // into view and the form jumped up. Focusing the trigger first makes the list return focus here
  const focusTriggerOnPress = (event: React.PointerEvent<HTMLDivElement>) => {
    const trigger = (event.target as HTMLElement).closest('button[data-slot="trigger"]');
    if (trigger instanceof HTMLElement && !selectProps.isDisabled) {
      trigger.focus({ preventScroll: true });
    }
  };

  return (
    <div className="w-full" onPointerDownCapture={focusTriggerOnPress}>
      <Select
        aria-label="Select"
        labelPlacement="outside"
        popoverProps={{ shouldBlockScroll: false }}
        selectedKeys={value}
        onSelectionChange={handleSelectionChange}
        items={items}
        isInvalid={isInvalid}
        classNames={{
          ...(style === 'black'
            ? classnamesBlackSelect
            : style === 'cream' || style === 'creamCompact'
              ? classnamesCreamSelect
              : style === 'purple'
                ? classnamesPurpleSelect
                : classnamesColorSelect),
          label: cn(styles.label, hideLabel && 'sr-only'),
          value: cn(
            Array.from(value).length > 0
              ? disableTextCurrent
                ? ''
                : '!text-current'
              : '!text-foreground-500',
          ),
          trigger: cn(
            style === 'black'
              ? styles.blackTrigger
              : style === 'cream'
                ? styles.creamTrigger
                : style === 'creamCompact'
                  ? styles.creamTriggerCompact
                  : style === 'purple'
                    ? styles.purpleTrigger
                    : cn(styles.colorTrigger, getColorTriggerStyle(value, items)),
            isInvalid ? '!border-danger' : '',
            selectProps.isDisabled && styles.disabledTrigger,
          ),
        }}
        {...selectProps}
      >
        {(item) => <SelectItem>{item.label}</SelectItem>}
      </Select>
      {isInvalid && showError && (
        <p className={cn('p-1 text-tiny', styles.errorMessage)} role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
});

CustomSelect.displayName = 'CustomSelect';
